import {
  Brand,
  PlatformBrand,
  PlatformListing,
  PriceTier,
  Product,
  Supplier,
} from './types';
import SupportedLanguages from '../../setup/intl/enums';

// Live mode maps the platform's ListingView → the storefront Product shape.
const DEFAULT_EMOJI = '📦';

// Seed/mock listings ship random picsum URLs as coverImageUrl — unrelated stock
// photos (a subway platform for hand wash). Treat them as "no image" so the UI
// falls back to an honest branded tile instead of a wrong picture.
const isRealImage = (url?: string | null): url is string =>
  !!url && !/picsum\.photos/i.test(url);

// Real images only, cover first, de-duplicated. Empty → branded fallback.
const resolveImages = (item: PlatformListing): string[] => {
  const out: string[] = [];
  if (isRealImage(item.coverImageUrl)) out.push(item.coverImageUrl);
  for (const u of item.images ?? []) {
    if (isRealImage(u) && !out.includes(u)) out.push(u);
  }
  return out;
};

// Deterministic pastel hue (0–360) from a string, so a brand/listing keeps a
// stable image background across renders.
const hueFrom = (s: string): number => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h) % 360;
};

export const mapPlatformListing = (item: PlatformListing): Product => {
  const tiers: PriceTier[] = [...item.pricingTiers]
    .sort((a, b) => a.minQty - b.minQty)
    .map((t) => ({ min: t.minQty, price: t.priceIqd }));
  const moq = item.moq ?? tiers[0]?.min ?? 1;

  return {
    id: item.id,
    name: item.name,
    nameAr: item.nameAr ?? '',
    brand: item.brand ?? '',
    cat: item.category ?? 'All',
    emoji: DEFAULT_EMOJI,
    hue: hueFrom(item.brand || item.category || item.id),
    moq,
    sold: item.sold ?? '',
    rating: item.rating ?? 0,
    reviews: item.reviews ?? 0,
    lead: item.leadTime ?? '',
    // Stock truth: undefined when inventory is absent (never fabricated).
    stock: item.inventory ? item.inventory.availableQty : undefined,
    stockUnit: item.inventory?.unit ?? undefined,
    tiers: tiers.length ? tiers : [{ min: moq, price: 0 }],
    desc: item.description ?? '',
    descAr: item.descriptionAr ?? '',
    images: resolveImages(item),
  };
};

export const mapPlatformBrand = (b: PlatformBrand): Brand => ({
  id: b.name,
  name: b.name,
  country: '',
  items: b.items,
  rating: 0,
  verified: false,
  hue: hueFrom(b.name),
  lead: '',
});

export const mapPlatformSupplier = (s: {
  id: string;
  name: string;
  city: string;
  tier: string;
}): Supplier => ({
  id: s.id,
  name: s.name,
  city: s.city,
  tier: s.tier,
});

// Arabic labels for the known live category slugs. Slugs not listed fall back
// to the prettified English (no fabricated translation). 'All' is the
// cross-category pill.
const CATEGORY_LABELS_AR: Record<string, string> = {
  All: 'الكل',
  beverages: 'مشروبات',
  snacks: 'وجبات خفيفة',
  household: 'منزلية',
  pantry: 'مؤن',
  'personal-care': 'عناية شخصية',
};

// Live API categories are slugs ("personal-care"); the server filters by the
// exact slug. Prettify the slug for the pill LABEL only — the filter VALUE
// stays the raw slug. (Verified: filtering by Title Case returns 0.)
export const categoryLabel = (slug: string, lang?: SupportedLanguages): string => {
  if (lang === SupportedLanguages.Arabic && CATEGORY_LABELS_AR[slug]) {
    return CATEGORY_LABELS_AR[slug];
  }
  if (slug === 'All') return slug;
  return slug
    .split('-')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
};

// Inventory stock-unit nouns (p.stockUnit from the platform), inflected for
// ICU plural branches: `unit` singular, `unitPair` dual (AR `two`), and
// `unitPlural` (AR `few`; EN `other`). AR `many`/`other` reuse the singular
// per Arabic count grammar. Unmapped units fall back to the raw value in every
// slot (English unchanged — never fabricate a translation).
export interface UnitForms {
  unit: string;
  unitPair: string;
  unitPlural: string;
}

const UNIT_FORMS: Record<string, { en: UnitForms; ar: UnitForms }> = {
  case: {
    en: { unit: 'case', unitPair: 'cases', unitPlural: 'cases' },
    ar: { unit: 'صندوق', unitPair: 'صندوقان', unitPlural: 'صناديق' },
  },
  unit: {
    en: { unit: 'unit', unitPair: 'units', unitPlural: 'units' },
    ar: { unit: 'وحدة', unitPair: 'وحدتان', unitPlural: 'وحدات' },
  },
};

const UNIT_ALIASES: Record<string, string> = { cases: 'case', units: 'unit' };

// Missing stockUnit falls back to the generic "unit/وحدة" forms (matches the
// old marketplace.stock.unit fallback).
export const unitForms = (
  unit: string | undefined,
  lang?: SupportedLanguages
): UnitForms => {
  const raw = (unit ?? 'unit').trim().toLowerCase();
  const entry = UNIT_FORMS[raw] ?? UNIT_FORMS[UNIT_ALIASES[raw]];
  if (!entry) return { unit: unit ?? raw, unitPair: unit ?? raw, unitPlural: unit ?? raw };
  return lang === SupportedLanguages.Arabic ? entry.ar : entry.en;
};

// Supplier cities arrive as raw lowercase slugs ("basra"). Arabic names for
// the known live cities (keyed case-insensitively); anything unmapped falls
// back to Title Case (never fabricate a translation). EN is always Title Case.
const CITY_LABELS_AR: Record<string, string> = {
  basra: 'البصرة',
  baghdad: 'بغداد',
  arbeel: 'أربيل',
  erbil: 'أربيل',
  mosul: 'الموصل',
  najaf: 'النجف',
};

export const cityLabel = (city: string, lang?: SupportedLanguages): string => {
  const key = city.trim().toLowerCase();
  if (lang === SupportedLanguages.Arabic && CITY_LABELS_AR[key]) {
    return CITY_LABELS_AR[key];
  }
  return city
    .trim()
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
};

// Supplier tier (Gold/Silver/Bronze enum) → Arabic. Feminine forms to agree
// with "فئة" in tierLabel ("مورّد فئة ذهبية"). Keyed lowercase so casing
// variants resolve; unknown tier falls back to the raw value (English
// unchanged — never fabricate a translation).
const TIER_LABELS_AR: Record<string, string> = {
  gold: 'ذهبية',
  silver: 'فضية',
  bronze: 'برونزية',
};

export const tierLabel = (tier: string, lang?: SupportedLanguages): string => {
  if (lang === SupportedLanguages.Arabic) {
    return TIER_LABELS_AR[tier.toLowerCase()] ?? tier;
  }
  return tier;
};
